import { Fragment, useMemo, useState } from 'react';

import { AsyncBoundary, Drawer, Icon, useToast } from '@kollez/design-system';
import { useApplications, type ApplicationListItem } from '@/lib/queries';
import { apiPatch } from '@/lib/api';

/**
 * /company/candidates (F90a) — direct port of kollez/pages/company/candidates.html.
 *   filterbar (search + campus + stage + 85%+match tag + count) · table with match badge
 *   · drawer w/ 3 kpi + Nexie fit summary + contact-hidden-till-shortlist
 *   · bulk-bar (Compare 2-4 / Shortlist / Not this time) · compare drawer
 */

type Stage = 'Applied' | 'Shortlisted' | 'Interview' | 'Offered' | 'Not this time';
const STAGE_TO_STATUS: Record<Stage, string> = {
  Applied: 'Under_Review',
  Shortlisted: 'Shortlisted',
  Interview: 'Interview_Scheduled',
  Offered: 'Offered',
  'Not this time': 'Rejected',
};
function statusToStage(status: string): Stage {
  const s = status.toLowerCase();
  if (/reject|withdraw/.test(s)) return 'Not this time';
  if (/offer|accept/.test(s)) return 'Offered';
  if (/interview/.test(s)) return 'Interview';
  if (/short|test|assess/.test(s)) return 'Shortlisted';
  return 'Applied';
}
function tone(stage: Stage): string {
  return stage === 'Shortlisted' ? 'badge-outline'
    : stage === 'Interview' ? 'badge-info'
    : stage === 'Offered' ? 'badge-success'
    : stage === 'Not this time' ? 'badge-danger'
    : '';
}

interface Row {
  _id: string;
  name: string;
  campus: string;
  branch: string;
  cgpa: number;
  match: number;
  stage: Stage;
  skills: string;
  highlights: string;
  hiddenContact: boolean;
}

function toRow(a: ApplicationListItem & { student?: { fullName?: string; branch?: string; cgpa?: number; skills?: string[] }; matchScore?: { overall: number }; drive?: { college?: { name?: string } } }): Row {
  const stage = statusToStage(a.status);
  return {
    _id: a._id,
    name: a.student?.fullName ?? '—',
    campus: a.drive?.college?.name ?? '—',
    branch: a.student?.branch ?? '—',
    cgpa: a.student?.cgpa ?? 0,
    match: a.matchScore?.overall ?? 0,
    stage,
    skills: (a.student?.skills ?? []).slice(0, 5).join(', '),
    highlights: '—',
    hiddenContact: stage === 'Applied',
  };
}

export function CompanyCandidatesPage() {
  const { data, isLoading, error, refetch } = useApplications();
  const toast = useToast();

  const [q, setQ] = useState('');
  const [campus, setCampus] = useState('');
  const [stage, setStage] = useState<'' | Stage>('');
  const [strong, setStrong] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openId, setOpenId] = useState<string | null>(null);
  const [cmpOpen, setCmpOpen] = useState(false);

  const rows: Row[] = useMemo(
    () => (data?.applications ?? []).map((a) => toRow(a as ApplicationListItem)),
    [data],
  );

  const campuses = useMemo(() => [...new Set(rows.map((r) => r.campus))].filter(Boolean).sort(), [rows]);

  const shown = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter((r) => {
      const okQ = !ql || `${r.name} ${r.skills}`.toLowerCase().includes(ql);
      const okCamp = !campus || r.campus === campus;
      const okStage = !stage || r.stage === stage;
      const okStrong = !strong || r.match >= 85;
      return okQ && okCamp && okStage && okStrong;
    });
  }, [rows, q, campus, stage, strong]);

  const opened = openId ? rows.find((r) => r._id === openId) : null;
  const compareRows = rows.filter((r) => selected.has(r._id));
  const canCompare = selected.size >= 2 && selected.size <= 4;

  function toggleSel(id: string) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }

  async function moveOne(id: string, to: Stage) {
    try {
      await apiPatch(`/applications/${id}`, { status: STAGE_TO_STATUS[to] });
      toast.show({ message: to === 'Shortlisted' ? 'Shortlisted' : 'Marked not this time. Student is told when results publish.' });
      refetch();
      setOpenId(null);
    } catch { toast.show({ tone: 'warning', message: 'Could not move' }); }
  }

  async function bulkMove(to: Stage) {
    const ids = [...selected];
    try {
      await Promise.all(ids.map((id) => apiPatch(`/applications/${id}`, { status: STAGE_TO_STATUS[to] })));
      toast.show({ message: `${ids.length} ${to === 'Shortlisted' ? 'shortlisted' : 'marked not this time'}` });
      setSelected(new Set());
      refetch();
    } catch { toast.show({ tone: 'warning', message: 'Bulk move failed' }); }
  }

  const maxMatch = Math.max(0, ...compareRows.map((r) => r.match));
  const maxCgpa = Math.max(0, ...compareRows.map((r) => r.cgpa));

  return (
    <div className="stack">
      <style>{`
        .cmp { display: grid; grid-template-columns: 120px repeat(var(--n), minmax(150px,1fr)); font-size: .875rem; overflow-x: auto; }
        .cmp > div { padding: 10px 12px; border-bottom: 1px solid rgb(var(--line-tertiary)); }
        .cmp .h { font-weight: 600; background: rgb(var(--bg-secondary)); }
        .cmp .k { color: rgb(var(--ink-tertiary)); }
        .cmp .best { color: rgb(var(--ink-success)); font-weight: 600; }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Candidates</h1>
          <p className="subhead">{rows.length} applications. Contact details show once a student is shortlisted.</p>
        </div>
        {/* Deep-audit fix (Page 43/N): 'Export shortlist' fired a lying
            toast saying 'shortlist_export.xlsx downloaded' — no file was
            generated, no API call. Recruiters expecting the file would
            check their downloads folder and find nothing. Disabled with
            'soon'. */}
        <div className="ph-actions">
          <button className="btn btn-outline" disabled title="Shortlist export ships in the next release.">
            <Icon name="download" size={14} />Export shortlist · soon
          </button>
        </div>
      </div>

      <div className="card">
        <div className="filterbar">
          <div className="input-group fb-search">
            <Icon name="search" size={14} />
            <input className="input" placeholder="Name or skill" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <select className="select" value={campus} onChange={(e) => setCampus(e.target.value)}>
            <option value="">All campuses</option>
            {campuses.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select className="select" value={stage} onChange={(e) => setStage(e.target.value as typeof stage)}>
            <option value="">Any stage</option>
            {(['Applied', 'Shortlisted', 'Interview', 'Offered'] as Stage[]).map((s) => <option key={s}>{s}</option>)}
          </select>
          <button className="tag" aria-pressed={strong} onClick={() => setStrong((v) => !v)}>85%+ match</button>
          <span className="fb-count">{shown.length} shown</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={isLoading}
          error={error}
          empty={!isLoading && shown.length === 0}
          emptyTitle="No applications yet"
          emptyHint="Applications arrive once a campus opens the drive to its students."
          onRetry={() => void refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th className="w-check"><span className="sr-only">Select</span></th>
                  <th>Candidate</th>
                  <th className="hide-mobile">Campus</th>
                  <th className="hide-mobile">CGPA</th>
                  <th>Match</th>
                  <th className="hide-mobile">Stage</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr
                    key={r._id}
                    onClick={(e) => { if ((e.target as HTMLElement).tagName !== 'INPUT') setOpenId(r._id); }}
                    aria-selected={selected.has(r._id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="w-check">
                      <input
                        type="checkbox"
                        className="check"
                        checked={selected.has(r._id)}
                        onChange={() => toggleSel(r._id)}
                        aria-label={`Select ${r.name}`}
                      />
                    </td>
                    <td>
                      <div className="cell-title">{r.name}</div>
                      <div className="cell-sub">
                        {r.branch} · {r.skills || '—'}
                        <span className="show-mobile inline"> · {r.campus}</span>
                      </div>
                    </td>
                    <td className="hide-mobile">{r.campus}</td>
                    <td className="hide-mobile">{r.cgpa.toFixed(2)}</td>
                    <td>
                      <span className="match" style={{ ['--v' as string]: `${r.match}%` } as React.CSSProperties}>
                        <i />{r.match}%
                      </span>
                    </td>
                    <td className="hide-mobile"><span className={`badge ${tone(r.stage)}`}>{r.stage}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      {selected.size > 0 && (
        <div className="bulkbar" data-open="true">
          <span className="text-sm whitespace-nowrap"><b>{selected.size}</b> selected</span>
          <button className="btn btn-ghost btn-sm" disabled={!canCompare} onClick={() => setCmpOpen(true)}>
            <Icon name="columns-3" size={14} />Compare
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => bulkMove('Shortlisted')}>
            <Icon name="check" size={14} />Shortlist
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => bulkMove('Not this time')}>
            <Icon name="x" size={14} />Not this time
          </button>
        </div>
      )}

      {/* Candidate drawer */}
      <Drawer open={Boolean(opened)} onClose={() => setOpenId(null)} title={opened?.name ?? ''} size="lg">
        {opened && (
          <>
            <p className="eyebrow">{opened.campus} · B.Tech {opened.branch} 2026</p>
            <h2 className="h-section">{opened.name}</h2>
            <div className="grid grid-cols-3 gap-2 mt-4">
              <div className="well p-3"><div className="helper">Match</div><b className="text-lg">{opened.match}%</b></div>
              <div className="well p-3"><div className="helper">CGPA</div><b className="text-lg">{opened.cgpa.toFixed(2)}</b></div>
              <div className="well p-3"><div className="helper">Stage</div><b>{opened.stage}</b></div>
            </div>
            <div className="ai-suggest mt-4">
              <Icon name="sparkles" size={14} />
              <div>
                <b>Fit summary.</b> {opened.skills || 'Skills on profile'} align with the role's core stack. {opened.highlights}.{' '}
                <span className="helper">From resume; verify in interview.</span>
              </div>
            </div>
            <dl className="dl mt-4">
              <dt>Skills</dt><dd>{opened.skills || '—'}</dd>
              <dt>Academics</dt><dd>Verified by the college</dd>
              {/* Deep-audit fix (Page 43/N): even AFTER shortlist, the
                  contact line rendered a hardcoded fake number
                  '+91 98 •••• 4521' identical for every candidate. A
                  recruiter reading that as a partial-mask over their
                  real number would try to guess digits and call a wrong
                  number. Also 'Message via college' fired a lying toast
                  with no API call. Both replaced with an honest
                  placeholder pointing at the working path (reach out
                  through the placement office directly). */}
              <dt>Contact</dt>
              <dd>
                {opened.hiddenContact ? (
                  <span className="helper">Shown after you shortlist</span>
                ) : (
                  <span className="helper">Reach out through the college's placement office. Direct-contact wire-up ships in the next release.</span>
                )}
              </dd>
            </dl>
            {/* Deep-audit fix (Page 43/N): Resume button fired a lying
                toast — 'Resume view opens once the candidate is
                shortlisted.' — but there's no resume-viewer route wired
                to this drawer even post-shortlist. Disabled with the
                honest description of the current state. */}
            <button
              type="button"
              className="btn btn-outline justify-self-start mt-4"
              disabled
              title="Resume viewer for shortlisted candidates ships in the next release."
            >
              <Icon name="file-text" size={14} />Resume · soon
            </button>
            <div className="drawer-foot mt-4">
              {opened.stage === 'Applied' ? (
                <>
                  <button className="btn btn-outline" onClick={() => moveOne(opened._id, 'Not this time')}>Not this time</button>
                  <button className="btn btn-primary ml-auto" onClick={() => moveOne(opened._id, 'Shortlisted')}>Shortlist</button>
                </>
              ) : (
                <span className="helper">{opened.stage} · managed by the college's schedule</span>
              )}
            </div>
          </>
        )}
      </Drawer>

      {/* Compare drawer */}
      <Drawer open={cmpOpen} onClose={() => setCmpOpen(false)} title={`Compare ${compareRows.length} candidates`} size="lg">
        <div className="cmp" style={{ ['--n' as string]: String(compareRows.length) } as React.CSSProperties}>
          {/* header row */}
          <div className="h" />
          {compareRows.map((r) => <div key={`h-${r._id}`} className="h">{r.name}</div>)}
          {[
            ['Campus', (r: Row) => r.campus],
            ['Branch', (r: Row) => r.branch],
            ['CGPA', (r: Row) => <span className={r.cgpa === maxCgpa ? 'best' : ''}>{r.cgpa.toFixed(2)}</span>],
            ['Match', (r: Row) => <span className={r.match === maxMatch ? 'best' : ''}>{r.match}%</span>],
            ['Skills', (r: Row) => r.skills || '—'],
            ['Highlights', (r: Row) => r.highlights],
            ['Stage', (r: Row) => r.stage],
          ].map(([label, fn]) => (
            <Fragment key={`row-${label as string}`}>
              <div className="k">{label as string}</div>
              {compareRows.map((r) => <div key={`${label}-${r._id}`}>{(fn as (r: Row) => React.ReactNode)(r)}</div>)}
            </Fragment>
          ))}
        </div>
        <div className="drawer-foot">
          <button className="btn btn-primary ml-auto" onClick={() => { setCmpOpen(false); bulkMove('Shortlisted'); }}>
            Shortlist all {compareRows.length}
          </button>
        </div>
      </Drawer>
    </div>
  );
}
